// Imports ------------------------------------------------------------------->

import {Component, attr, handler} from "../Component";
import {element} from "../CustomElement";
import {i18n, ll} from "../../i18n/I18n";
import {icons} from "../../icons/Icons";
import {isHeroCoupon, breakLines} from "../../utils/coupons";

import {Dialog, DialogHeader} from "../flipp-dialog/Dialog";
import {ToastContainer} from "../flipp-toast-container/ToastContainer";
import {Link, ButtonLink} from "../flipp-link/Link";
import {LazyImage} from "../flipp-lazy-image/LazyImage";
import {ClipCouponButton} from "../flipp-clip-button/ClipCouponButton";
import {CouponClipElement} from "../flipp-coupon-clipper/CouponClipElement";

import {inject} from "../../services/di";
import {AnalyticsService} from "../../services/AnalyticsService";
import {AuthenticationService} from "../../services/AuthenticationService";
import {AuthStatus} from "../../services/definitions/IAuthenticationService";
import {CouponService} from "../../services/CouponService";
import {LoyaltyProgramService} from "../../services/LoyaltyProgramService";
import {ClippingService} from "../../services/ClippingService";
import {LocationService} from "../../services/LocationService";
import {Coupon, ActiveClippedCoupons, CouponRedemptionStatus, CouponRedemptionMethod} from "../../definitions/CouponDefinitions";
import {CouponActions, CouponSource} from "../../services/definitions/IAnalyticsService";
import { isCrawler } from "../../utils/crawler";

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

// --- flipp-coupon-clipping  ------------------------------------------------>

export type CouponClippingContext = "full" | "pop";

interface CouponClippingAttributes extends JSX.HTMLProps {
	couponId: number;
	hidematchup?: boolean;
	hidebadges?: boolean;
	context?: CouponClippingContext;
	source?: CouponSource;
}

type CouponClippingModel = [Coupon, ActiveClippedCoupons];

@element("flipp-coupon-clipping")
export class CouponClipping extends Component<CouponClippingAttributes, CouponClippingModel> {
	@attr couponId: number;
	@attr hidematchup: boolean;
	@attr hidebadges: boolean;
	@attr context: CouponClippingContext;
	@handler source: CouponSource;

	@inject couponService: CouponService;
	@inject auth: AuthenticationService;
	@inject loyaltyService: LoyaltyProgramService;
	@inject clippingService: ClippingService;
	@inject analytics: AnalyticsService;
	@inject location: LocationService;

	refs: {
		link: Link | ButtonLink;
	};

	private _activeCouponPop: Dialog;

	prepare() {
		let getClippedCouponsFn = Promise.resolve<ActiveClippedCoupons>([]);
		if (this.auth.status === AuthStatus.LOGGED_IN) {
			getClippedCouponsFn = this.couponService.getActiveClippedCoupons(this.auth.user);
		}
		return Promise.all([
			this.couponService.getCoupon(this.couponId),
			getClippedCouponsFn
		]);
	}

	onRender() {
		if (this.context === "pop") {
			const link = this.refs.link as ButtonLink;
			link.onclick = () => {
				const [coupon] = this.model;
				this._activeCouponPop = this.getCouponPop(coupon, true) as Dialog;
				this._activeCouponPop.addEventListener("flipp-navigate", this._handleDialogClose);
				this._activeCouponPop.showModal();
				this.analytics.trackCoupon(coupon, CouponActions.Open, this.source);
			};
		}
	}

	render([coupon, clippedCoupons]: CouponClippingModel) {
		const matchup = this.hidematchup === false &&
			coupon.matchUps.length > 0 ? coupon.matchUps[0] : null;
		const isHero = this.hidematchup ? false : isHeroCoupon(coupon);

		let promoLines = coupon.promotionText ? breakLines(coupon.promotionText, 24,
				(isHero || !matchup) ? 3 : 2) : [];
		let saleStoryLines = coupon.saleStory ? breakLines(coupon.saleStory, 24, 3) : [];
		if (saleStoryLines.length >= 2 && !coupon.dollarsOff && !coupon.percentOff) {
			/* If coupon has a sale story greater than 2 lines, we cannot show any promo lines */
			promoLines = [];
		}

		const CouponPopLink = this.context === "pop" ? ButtonLink : Link;
		const clipped = this.clippingService.hasClipping(`${coupon.id}`) !== undefined || (clippedCoupons[coupon.id] ?
			clippedCoupons[coupon.id].status === CouponRedemptionStatus.CLIPPED : false);
		const redeemed = clippedCoupons[coupon.id] ?
			clippedCoupons[coupon.id].status === CouponRedemptionStatus.REDEEMED : false;

		return <div
			class="coupon-container"
			classList={{
				hero: matchup && isHero,
				peek: matchup && !isHero,
				clipped: this.hidebadges ? false : clipped,
				redeemed: this.hidebadges ? false : redeemed
			}}
			tabIndex={0}>
			<CouponPopLink ref="link" class="coupon" to={coupon}>
				<Link to={coupon}>
					<div class="image">
						<LazyImage alt={this.getAlt(coupon)} dataSrc={coupon.imageUrl} disableLazyLoading />
					</div>

					<div class="info">
						{isCrawler() ? <h3>{this.getH3(coupon)}</h3> : null }
						<p class="save">{i18n("save")}</p>
						{ coupon.dollarsOff !== null ?
							<flipp-price value={coupon.dollarsOff.toFixed(2)} />
						: coupon.percentOff !== null ?
							<p class="percent-off">{`${(coupon.percentOff * 100).toFixed(0)}%`}</p>
						: coupon.saleStory ?
							saleStoryLines.map((line) => <p class="sale-story">{line}</p>)
						:
							null
						}

						{ promoLines.map((line, index) => {
							return <p class="promotion-text">{line}</p>;
						})}
					</div>
				</Link>


				<span class="type">
					{!this.hidebadges && redeemed ?
						<span class="tag redeemed">{i18n("redeemed")}</span>
					: !this.hidebadges && clipped ?
						<span class="tag clipped">{i18n("clipped")}</span>
					: coupon.redemptionMethod === CouponRedemptionMethod.PRINT ?
								<img class="icon" src="https://f.wishabi.net/creative/Distribution/images/icons/print-default5.svg" alt=""/>
					: coupon.redemptionMethod === CouponRedemptionMethod.SAVE_TO_CARD ?
									<img class="icon" src="https://f.wishabi.net/creative/Distribution/images/icons/card.svg" alt=""/>
					: null }
				</span>
			</CouponPopLink>

			{ (isHero && !isCrawler()) ?
				<Link class="matchup"
							to={matchup.flyer}
							params={{
								item_id: matchup.item.id}}>
					<header>
						<span class="percent-off">{(matchup.percentOff * 100).toFixed(0)}% Off</span>
						<h2>
							<p>{i18n("matchup_at")}</p>
							<p class="retailer">{matchup.merchant}</p>
						</h2>
					</header>
					<div class="pricing">
						{ matchup.item.originalPrice !== null &&
							matchup.item.dollarsOff !== null ?
							<div>
								<p>{i18n("regular_price")} <flipp-price class="strike-out" value={matchup.item.originalPrice.toFixed(2)} /></p>
								<p>{i18n("sale_price")} <flipp-price class="off" value={(-matchup.item.dollarsOff).toFixed(2)} /></p>
							</div>
						:
							<p>{i18n("current_price")} <flipp-price class="strike-out" value={matchup.item.currentPrice.toFixed(2)} /></p>
						}

						<p>{i18n("coupon_price")} <flipp-price class="off" value={(-matchup.couponDollarsOff).toFixed(2)} /></p>
					</div>
					<p class="final-price">
						{i18n("final_price")} <flipp-price value={matchup.finalPrice.toFixed(2)} />
					</p>
				</Link>
			: matchup ?
					(isCrawler() ? null :
						<Link class="matchup-peek"
									to={matchup.flyer}
									params={{
										item_id: matchup.item.id}}>
							<h2>{i18n("matchup_at")} <span class="retailer">{matchup.merchant}</span></h2>
						</Link>
					)
			: null }
		</div>;
	}

	private getCouponPop = (coupon: Coupon, showterms = true) => {
		return <Dialog class="coupon-pop" transient>
				<DialogHeader>
					<div content-slot="title">{i18n("coupon_details_title")}</div>
				</DialogHeader>

				<ToastContainer>
					<CouponClippingDialogContent couponId={this.couponId} showterms={showterms} />
				</ToastContainer>

			</Dialog>;
	}

	private _handleDialogClose = () => {
		if (this._activeCouponPop && this._activeCouponPop.open) {
			this._activeCouponPop.removeEventListener("flipp-navigate", this._handleDialogClose);
			this._activeCouponPop.close();
		}
		this._activeCouponPop = null;
	}

	private getAlt = (coupon: Coupon) => {
		return i18n("search_page_alt", {
			city: this.location.city,
			itemName: coupon.getName(),
		});
	}

	private getH3 = (coupon: Coupon) => {
		const status = coupon.getExpired() ? i18n("expired") : i18n("online");
		return i18n("search_page_h3", {
			itemName: coupon.getName(),
			status
		});
	}
}

// --- flipp-coupon-clipping-dialog-content --------------------------------->

interface CouponClippingDialogContentAttributes extends JSX.HTMLProps {
	couponId: number;
	showterms?: boolean;
}

interface CouponClippingDialogContentModel {
	coupon: Coupon;
	clipped: boolean;
	redeemed: boolean;
	terms: string;
}

@element("flipp-coupon-clipping-dialog-content")
export class CouponClippingDialogContent extends Component<CouponClippingDialogContentAttributes, CouponClippingDialogContentModel> {

	@attr couponId: number;
	@attr showterms: boolean;
	@inject location: LocationService;
	@inject couponService: CouponService;
	@inject auth: AuthenticationService;
	@inject loyaltyService: LoyaltyProgramService;
	@inject clippingService: ClippingService;
	@inject analytics: AnalyticsService;

	prepare() {
		let getClippedCouponsFn = Promise.resolve<ActiveClippedCoupons>([]);
		if (this.auth.status === AuthStatus.LOGGED_IN) {
			getClippedCouponsFn = this.couponService.getActiveClippedCoupons(this.auth.user);
		}

		let termsFn = Promise.resolve<string>("");
		if (this.showterms) {
			/* Only fetch TnC if explicitly requested as it requires an additional fetch to backflipp */
			termsFn = this.couponService.getCouponDisclaimer(`${this.couponId}`);
		}

		return Promise.all([
			this.couponService.getCoupon(this.couponId),
			getClippedCouponsFn,
			termsFn
		]).then(([coupon, clippedCoupons, terms]) => {
			const clipped = this.clippingService.hasClipping(`${coupon.id}`) !== undefined || (clippedCoupons[coupon.id] ?
				clippedCoupons[coupon.id].status === CouponRedemptionStatus.CLIPPED : false);
			const redeemed = clippedCoupons[coupon.id] ?
				clippedCoupons[coupon.id].status === CouponRedemptionStatus.REDEEMED : false;

			return {
				coupon,
				clipped,
				redeemed,
				terms
			};
		});
	}

	onAttach() {
		this.addEventListener("flipp-auth", this.checkClippingStatus_);
		this.addEventListener("flipp-loyaltycards", this.checkClippingStatus_);
		this.addEventListener("flipp-couponclipped", this._handlePrintClipped);
	}

	onDetach() {
		this.removeEventListener("flipp-auth", this.checkClippingStatus_);
		this.removeEventListener("flipp-loyaltycards", this.checkClippingStatus_);
		this.removeEventListener("flipp-couponclipped", this._handlePrintClipped);
	}

	renderLoading() {
		return <div class="inner" flex-horizontal>
				<div class="coupon-loading">
					<div class="spinner"></div>
				</div>
			</div>;
	}

	render({coupon, clipped, redeemed, terms}: CouponClippingDialogContentModel) {
		const printUrl = `https://share.flipp.com/flipp/coupons/redeem?coupon_ids=${coupon.id}&locale=${this.location.locale}`;
		return <div class="inner" classList={{clipped: clipped}}>
			<div class="image">
				<LazyImage alt="" dataSrc={coupon.imageUrl} />
			</div>
			<div class="info" scrollable>
				<div class="tags" flex-horizontal>
					{clipped ?
						<div class="tag clipped">{i18n("clipped")}</div>
					: redeemed ?
						<div class="tag redeemed">{i18n("redeemed")}</div>
					:
						null
					}
					<div class="type">
						{ coupon.redemptionMethod === CouponRedemptionMethod.PRINT ?
							<img class="icon" src="https://f.wishabi.net/creative/Distribution/images/icons/print-default5.svg" alt=""/>
						: coupon.redemptionMethod === CouponRedemptionMethod.SAVE_TO_CARD ?
								<img class="icon" src="https://f.wishabi.net/creative/Distribution/images/icons/card.svg" alt=""/>
						: null }
					</div>
				</div>
				<div class="coupon-save">
					<p class="save">{i18n("save")}</p>
					{ coupon.dollarsOff !== null ?
						<flipp-price value={coupon.dollarsOff.toFixed(2)} />
					: coupon.percentOff !== null ?
						<p class="percent-off">{`${(coupon.percentOff * 100).toFixed(0)}%`}</p>
					: coupon.saleStory ?
						<p class="sale-story">{coupon.saleStory}</p>
					:
						null
					}
				</div>
				{isCrawler() ? this.getH1() : null}
				<p class="promotion-text">{coupon.promotionText}</p>
				{coupon.validFrom || coupon.validTo ?
					<div class="coupon-validity">
						{i18n("flexible_validity", {
								from: ll(coupon.validFrom),
								to: ll(coupon.validTo)
						})}
					</div>
				:
					null
				}

				{ coupon.redemptionMethod === CouponRedemptionMethod.PRINT ?
					<div class="action-buttons">
						<a class="ttm-button"
							href={printUrl}
							onClick={this._handlePrintClick}
							target="_blank">{i18n("print_coupon")}</a>
						<ClipCouponButton couponId={coupon.id} />
					</div>
				: coupon.redemptionMethod === CouponRedemptionMethod.SAVE_TO_CARD && !redeemed ?
					<CouponClipElement couponId={coupon.id} />
				: null }

				{ coupon.matchUps.length > 0 ?
					<section class="matchups">
						<h2>{i18n("matching_coupons")}</h2>
						{coupon.matchUps.map((matchup) =>
							isCrawler() ? null :
								<Link class="matchup-peek" flex-horizontal
											to={matchup.flyer}
											params={{
												item_id: matchup.item.id}}>
									<LazyImage alt="" dataSrc={matchup.item.cutoutUrl} />

									<div class="info">
										{ matchup.displayFinalPrice && matchup.finalPrice ?
											<p>{i18n("final_price")} <flipp-price value={matchup.finalPrice.toFixed(2)} /></p>
										:
											<p>{i18n("matchup_at")}</p>
										}
										<p class="store">{matchup.merchant}</p>
									</div>
								</Link>
						)}
					</section>
				: null }

				{ terms ?
					<section class="tnc">
						{terms}
					</section>
				:
					null
				}
			</div>
		</div>;
	}

	private _handlePrintClick = (e: Event) => {
		this.analytics.trackCouponPrint();
	}

	private _handlePrintClipped = (e: Event) => {
		const {coupon} = this.model;
		if (coupon.redemptionMethod === CouponRedemptionMethod.PRINT) {
			const isClipped = this.clippingService.getClipping(`${coupon.id}`);
			if (isClipped) {
				this.analytics.trackCoupon(coupon, CouponActions.Add, CouponSource.CouponDetails);
			}
		}
		this.invalidate();
	}

	private checkClippingStatus_ = (e: CustomEvent) => {
		this.invalidate();
	}

	private getH1() {
		const {coupon} = this.model;
		const h1 = i18n("coupon_page_h1", {
			name: coupon.brand ? coupon.brand : coupon.getName(),
		});
		return <h1>{h1}</h1>;
	}
}
